import { FC, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useUserNotifications } from '../../hooks/notifications/useNotifications'
import { useConfirmAction } from '../../hooks/utils/useConfirmAction'
import { defaultProfileImage } from '../../utils/constants'
import NotificationsSettingsModal from './NotificationsSettingsModal'

dayjs.extend(relativeTime)

// ============================================================================
// Seguridad: allowlist para target_url
// ----------------------------------------------------------------------------
// target_url viene del backend pero podria ser manipulado (via una notificacion
// creada por otro user, un actor comprometido o un futuro webhook). Un valor
// como `javascript:alert(1)` o `https://evil.tld/phish` pasado a
// history.push abriria un vector XSS / open-redirect.
//
// Regla estricta: SOLO rutas internas relativas (empiezan con `/`, sin
// protocolo, sin `//`, sin whitespace). Cualquier otra forma se descarta y el
// click no navega.
// ============================================================================
const INTERNAL_PATH_REGEX = /^\/(?!\/)[A-Za-z0-9/_\-?=&.%:]*$/

const isSafeInternalPath = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  if (value.length === 0 || value.length > 512) return false
  if (/\s/.test(value)) return false
  return INTERNAL_PATH_REGEX.test(value)
}

interface NotificationRecord {
  id: number
  verb?: string
  description?: string
  timestamp?: string
  created_at?: string
  createdAt?: string
  target_url?: string | null
  targetUrl?: string | null
  actor?: {
    avatar?: string | null
    name?: string | null
  } | null
  actor_avatar?: string | null
  actor_name?: string | null
  isRead?: boolean
  is_read?: boolean
}

const formatRelative = (record: NotificationRecord): string => {
  const raw = record.created_at || record.createdAt || record.timestamp
  if (!raw) return ''
  const parsed = dayjs(raw)
  if (!parsed.isValid()) return String(raw)
  return parsed.fromNow()
}

const NotificationsPage: FC = () => {
  // El layout (header + aside + footer) se resuelve en routeLayouts.ts como
  // LAYOUT_CONFIGS.PROFILE. Antes se forzaba aqui con setLayout sobre un
  // config congelado, lo que a veces pisaba el chrome y dejaba la pantalla
  // huerfana (sin forma de volver al resto de la app). Ahora la ruta hereda
  // el layout PROFILE del mapa central igual que /my-bookings y /my-expertise.
  const history = useHistory()
  const location = useLocation()
  const confirmAction = useConfirmAction()
  const { data, loading, deleteById, refetch } = useUserNotifications()
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false)

  // Deep-link opcional: /notifications?settings=1 abre el modal directamente.
  // Lo usa HeaderUserMenu (menos intrusivo que colgar estado global solo por
  // un modal). Limpiamos el query param al cerrar para que un back/forward no
  // reabra el modal en loop.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('settings') === '1') {
      setSettingsOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const handleCloseSettings = () => {
    setSettingsOpen(false)
    const params = new URLSearchParams(location.search)
    if (params.has('settings')) {
      params.delete('settings')
      const nextSearch = params.toString()
      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      })
    }
  }

  const items: NotificationRecord[] = useMemo(() => {
    if (!Array.isArray(data)) return []
    // Copia + orden created_at desc; si created_at falta, va al final.
    return [...data].sort((a, b) => {
      const ta = dayjs(a.created_at || a.createdAt || a.timestamp || 0).valueOf()
      const tb = dayjs(b.created_at || b.createdAt || b.timestamp || 0).valueOf()
      return tb - ta
    })
  }, [data])

  // UAT-269: click seguro en el body de la notif.
  const handleItemClick = (record: NotificationRecord) => {
    const raw = record.target_url ?? record.targetUrl
    if (!isSafeInternalPath(raw)) {
      // target_url null, ausente o unsafe: no navega. Log solo en dev.
      if (process.env.NODE_ENV === 'development') {
        // No log del valor bruto en produccion (evita eco de payloads
        // potencialmente sensibles al operador).
        // eslint-disable-next-line no-console
        console.debug('[notifications] skipped navigation (unsafe/empty target_url)')
      }
      return
    }
    history.push(raw)
  }

  const handleDeleteClick = async (
    e: React.MouseEvent,
    record: NotificationRecord
  ) => {
    e.stopPropagation()
    const confirmed = await confirmAction({
      title: 'Delete notification',
      message: 'This notification will be removed from your list. This action cannot be undone.',
      btnTextAction: 'Delete',
      btnTextDecline: 'Cancel',
    })
    if (!confirmed) return
    try {
      await deleteById(record.id)
    } catch (err) {
      // El hook ya hizo rollback; feedback minimo al usuario via console
      // (no exponemos stack traces en la UI).
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('[notifications] delete failed', err)
      }
    }
  }

  // Sidebar counter (pendientes de experiences). Sin endpoint dedicado en
  // este PR: TODO explicito, badge en "--" hasta PR-I-65 o siguiente.
  const pendingExperiencesCount: number | null = null

  const handleSettingsClick = () => {
    setSettingsOpen(true)
  }

  const resolveAvatar = (record: NotificationRecord): string => {
    return (
      record.actor?.avatar ||
      record.actor_avatar ||
      defaultProfileImage
    )
  }

  const resolveActorName = (record: NotificationRecord): string => {
    return record.actor?.name || record.actor_name || ''
  }

  const hasClickableTarget = (record: NotificationRecord): boolean => {
    return isSafeInternalPath(record.target_url ?? record.targetUrl)
  }

  return (
    <div className='nb-notifications-page'>
      <div className='nb-notifications-page__container'>
        {/* MAIN COLUMN */}
        <section className='nb-notifications-page__main'>
          <header className='nb-notifications-page__header'>
            <h1 className='nb-notifications-page__title'>Notifications</h1>
          </header>

          <div className='nb-notifications-page__list' role='list'>
            {loading && items.length === 0 && (
              <>
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={`skeleton-${i}`}
                    className='nb-notifications-page__skeleton'
                    aria-hidden='true'
                  />
                ))}
              </>
            )}

            {!loading && items.length === 0 && (
              <div className='nb-notifications-page__empty'>
                You have no notifications yet.
              </div>
            )}

            {items.map((record) => {
              const clickable = hasClickableTarget(record)
              const actorName = resolveActorName(record)
              return (
                <div
                  key={record.id}
                  role='listitem'
                  className={`nb-notifications-page__item ${
                    clickable ? 'nb-notifications-page__item--clickable' : ''
                  }`}
                  onClick={clickable ? () => handleItemClick(record) : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleItemClick(record)
                          }
                        }
                      : undefined
                  }
                  tabIndex={clickable ? 0 : -1}
                >
                  <img
                    className='nb-notifications-page__item-avatar'
                    src={resolveAvatar(record)}
                    alt={actorName ? `${actorName} avatar` : 'notification avatar'}
                    onError={(e) => {
                      const img = e.currentTarget
                      if (img.src !== defaultProfileImage) {
                        img.src = defaultProfileImage
                      }
                    }}
                  />
                  <div className='nb-notifications-page__item-body'>
                    <div className='nb-notifications-page__item-verb'>
                      {record.verb || record.description || 'You have a new notification.'}
                    </div>
                    {record.verb && record.description && (
                      <div className='nb-notifications-page__item-description'>
                        {record.description}
                      </div>
                    )}
                    <div className='nb-notifications-page__item-time'>
                      {formatRelative(record)}
                    </div>
                  </div>
                  <button
                    type='button'
                    className='nb-notifications-page__item-delete'
                    aria-label='Delete notification'
                    onClick={(e) => handleDeleteClick(e, record)}
                  >
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      aria-hidden='true'
                    >
                      <path
                        d='M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </button>
                </div>
              )
            })}
          </div>
        </section>

        {/* SIDEBAR RIGHT */}
        <aside className='nb-notifications-page__sidebar' aria-label='Requests and invitations'>
          <div className='nb-notifications-page__sidebar-title'>REQUESTS / INVITATIONS</div>
          <button
            type='button'
            className='nb-notifications-page__sidebar-card'
            onClick={() => history.push('/experiences')}
          >
            <div className='nb-notifications-page__sidebar-card-badge'>
              <span className='nb-notifications-page__sidebar-card-badge-number'>
                {pendingExperiencesCount === null ? '--' : pendingExperiencesCount}
              </span>
            </div>
            <div className='nb-notifications-page__sidebar-card-text'>
              Pending Experiences Requests
            </div>
          </button>
          {/*
            TODO: expone conteo real cuando el endpoint /notifications/settings/
            o un /experiences/requests/pending/count/ este disponible. Por ahora
            no lanzamos fetch extra para no crear ruido en el backend.
          */}
          {/*
            Settings link movido aqui desde el header (mockup NO-03): el link
            vive como ultimo elemento del sidebar debajo del card de Pending
            Experiences. Sigue siendo un boton (no <a>) para preservar la
            semantica original — no navega URL, abre el modal.
          */}
          <button
            type='button'
            className='nb-notifications-page__settings-link'
            onClick={handleSettingsClick}
          >
            Notifications settings
          </button>
        </aside>
      </div>

      {/*
        Refetch defensivo: si el usuario vuelve a la pagina despues de haber
        interactuado con una notif que hizo history.push a otra ruta, este
        botton oculto le da al orquestador la posibilidad de dispararlo desde
        eventos futuros (WebSocket, background sync). Por ahora no se usa,
        pero refetch queda expuesto en el hook para PR-I-65.
      */}
      <span style={{ display: 'none' }} aria-hidden='true' data-refetch={typeof refetch} />

      <NotificationsSettingsModal
        show={settingsOpen}
        onClose={handleCloseSettings}
      />
    </div>
  )
}

export default NotificationsPage
